import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import EditIcon from '@material-ui/icons/Edit';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { GET_TASK_FIELDS } from '../get-task-fields.gql';
import { AddressInputDialog } from '../components/AddressInputDialog';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(4),
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    maxHeight: '500px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  count: {
    fontSize: '16px',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

interface Props {
  onImport: (data: any) => void;
  onBack: () => void;
  data: any[];
  setData: (data: any[]) => void;
  isImporting: boolean;
}

export function ReviewStep({ onImport, onBack, data, setData, isImporting }: Props) {
  const classes = useStyles();
  const [addressDialog, setAddressDialog] = React.useState<{
    open: boolean;
    rowIndex: number;
    fieldId: string;
    value: string;
  } | null>(null);
  const [taskFields, setTaskFields] = React.useState<Record<string, any>>({});

  const [fetchTaskFields] = useCampfireLazyQuery(GET_TASK_FIELDS);

  // Fetch task field definitions for each unique taskId
  React.useEffect(() => {
    const uniqueTaskIds = [...new Set(data.map((row) => row.taskId))];
    uniqueTaskIds.forEach((taskId) => {
      if (!taskFields[taskId]) {
        fetchTaskFields({
          variables: { taskId },
        }).then((response) => {
          if (response.data?.vm?.task?.taskItems) {
            // Extract fields from taskItems
            const fields = response.data.vm.task.taskItems
              .filter((item: any) => item?.field)
              .map((item: any) => item.field);
            setTaskFields((prev) => ({
              ...prev,
              [taskId]: fields,
            }));
          }
        });
      }
    });
  }, [data, fetchTaskFields, taskFields]);

  const isAddressField = (taskId: string, fieldId: string): boolean => {
    const fields = taskFields[taskId];
    if (!fields) return false;
    const field = fields.find((f: any) => f.taskFieldId === fieldId);
    return field?.__typename === 'VOLUNTEER_AddressTaskFieldType';
  };

  const processData = () => {
    const processed = data.map((row) => {
      const { profileId, taskId, ...fieldValues } = row;
      return {
        profileId,
        taskId,
        fieldValues,
      };
    });
    return processed;
  };

  const handleImport = () => {
    const processedData = processData();
    onImport(processedData);
  };

  const handleOpenAddressDialog = (rowIndex: number, fieldId: string, value: string) => {
    setAddressDialog({
      open: true,
      rowIndex,
      fieldId,
      value,
    });
  };

  const handleSaveAddress = (addressJson: string) => {
    if (addressDialog) {
      const updatedData = [...data];
      updatedData[addressDialog.rowIndex][addressDialog.fieldId] = addressJson;
      setData(updatedData);
      setAddressDialog(null);
    }
  };

  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Review Task Data</Typography>
      <Typography className={classes.subtitle}>
        Review the task data below before importing. Each row should have a profileId, taskId, and field values.
      </Typography>

      <Typography className={classes.count}>{data.length} tasks to import</Typography>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow key={`${row.profileId}-${row.taskId}`}>
                {columns.map((column) => {
                  const isAddress = column !== 'profileId' && column !== 'taskId' && isAddressField(row.taskId, column);
                  return (
                    <TableCell key={column}>
                      {isAddress ? (
                        <Box display='flex' alignItems='center'>
                          <Typography variant='body2' style={{ marginRight: 8 }}>
                            {row[column] ? 'Address set' : 'No address'}
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={() => handleOpenAddressDialog(rowIndex, column, row[column] || '')}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      ) : (
                        row[column]
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {addressDialog && (
        <AddressInputDialog
          open={addressDialog.open}
          onClose={() => setAddressDialog(null)}
          onSave={handleSaveAddress}
          initialValue={addressDialog.value}
        />
      )}

      <Box className={classes.buttonContainer}>
        <Button variant='outlined' onClick={onBack} disabled={isImporting}>
          Back
        </Button>
        <Button variant='contained' color='primary' onClick={handleImport} disabled={isImporting || data.length === 0}>
          {isImporting ? 'Importing...' : `Import ${data.length} Tasks`}
        </Button>
      </Box>
    </Box>
  );
}
